import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, RefreshCw, Eye, Loader2, FileText, Package,
  Printer, Usb, Network as NetworkIcon, Boxes,
  AlertTriangle, Trash2, ShieldCheck, CheckCircle2, UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { validateStaffId } from '@/services/validationService';
import * as printerApi from '@/services/printerApi';
import * as networkPrintApi from '@/services/networkPrintApi';
import { generateTsplFromLabelData, PRINTER_CONFIGS, templateHasLayout } from '@/services/tsplLabel';
import NetworkPrinterDialog from '@/components/print/NetworkPrinterDialog';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
};

const STATUS_STYLES = {
  OPEN: 'bg-amber-100 text-amber-700 border-amber-300',
  FINALIZED: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};

const SalesReturnTransactions = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [skip, setSkip] = useState(0);
  const LIMIT = 25;
  const debounceRef = useRef(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // OPEN-receipt finalize blockers (untagged / needs-FG USNs) + resolve state
  const [blockers, setBlockers] = useState(null);
  const [resolvingUsn, setResolvingUsn] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  // Floor accountability — grade/remove/finalize require a validated Staff ID.
  // Pre-fill from any station scan earlier in this tab session.
  const [resolveStaffId, setResolveStaffId] = useState(() => sessionStorage.getItem('module_staff_id') || '');
  const [resolveStaffName, setResolveStaffName] = useState(() => sessionStorage.getItem('module_staff_name') || '');
  const [staffInput, setStaffInput] = useState('');
  const [verifyingStaff, setVerifyingStaff] = useState(false);

  // --- Passive sticker reprint (same sticker, no data change) ---
  const [printMethod, setPrintMethod] = useState(() => localStorage.getItem('sr_print_method') || 'usb');
  const [netPrinter, setNetPrinter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sr_net_printer') || 'null'); } catch (_) { return null; }
  });
  const [usbDevice, setUsbDevice] = useState(null);
  const [usbSupported, setUsbSupported] = useState(true);
  const [printerPickerOpen, setPrinterPickerOpen] = useState(false);
  const printerPickerResolve = useRef(null);
  const [printingSku, setPrintingSku] = useState(null);
  const [arialLineSpacingMultiplier, setArialLineSpacingMultiplier] = useState(2.5);
  // Combo box-sticker second step — { sku, boxTemplate, label_data, mrp_formatted, usns[] }
  const [pendingBoxPrint, setPendingBoxPrint] = useState(null);
  const printerModel = 'TSC_TE210';

  useEffect(() => {
    setUsbSupported(printerApi.isWebUSBSupported());
    apiClient.get('/print-settings')
      .then((r) => {
        const m = r.data?.settings?.arial_line_spacing_multiplier;
        if (m) setArialLineSpacingMultiplier(m);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit: LIMIT };
      if (status !== 'all') params.status = status;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (search.trim()) params.search = search.trim();
      const res = await apiClient.get('/sales-return/transactions', { params });
      setRows(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load transactions');
    } finally { setLoading(false); }
  }, [skip, status, fromDate, toDate, search]);

  // Debounced reload on filter/search change.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 300);
    return () => clearTimeout(debounceRef.current);
  }, [load]);

  const fetchBlockers = async (rid) => {
    try {
      const res = await apiClient.get(`/sales-return/receipts/${rid}/untagged`);
      setBlockers(res.data);
    } catch (_) { setBlockers(null); }
  };

  // Validate a Staff ID once; apiClient then attaches X-Staff-Id to every
  // grade/remove/finalize call (backend staff_scan_dep requires it).
  const verifyStaff = async () => {
    const v = staffInput.trim();
    if (!v) return;
    setVerifyingStaff(true);
    try {
      const res = await validateStaffId(v);
      if (!res.valid) { toast.error(res.message || 'Invalid Staff ID'); return; }
      sessionStorage.setItem('module_staff_id', res.staffId);
      sessionStorage.setItem('module_staff_name', res.staffName || '');
      setResolveStaffId(res.staffId);
      setResolveStaffName(res.staffName || '');
      setStaffInput('');
      toast.success(`Verified: ${res.staffName}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Staff validation failed');
    } finally { setVerifyingStaff(false); }
  };

  const changeResolveStaff = () => { setResolveStaffId(''); setResolveStaffName(''); };

  const refreshDetail = async (rid, rowStatus) => {
    const res = await apiClient.get(`/sales-return/receipts/${rid}/item-summary`);
    setDetailData(res.data);
    if (rowStatus === 'OPEN') await fetchBlockers(rid);
    else setBlockers(null);
  };

  const openDetail = async (row) => {
    setDetailRow(row);
    setDetailOpen(true);
    setDetailData(null);
    setBlockers(null);
    setDetailLoading(true);
    try {
      await refreshDetail(row.receipt_id, row.status);
    } catch (e) {
      toast.error('Failed to load transaction details');
    } finally { setDetailLoading(false); }
  };

  // Grade a single blocking USN right from the transaction modal.
  const gradeUsn = async (usnCode, quality) => {
    setResolvingUsn(usnCode);
    try {
      await apiClient.patch(
        `/sales-return/receipts/${detailRow.receipt_id}/usns/${usnCode}/quality`,
        { quality_status: quality });
      toast.success(`${usnCode} → ${quality}`);
      await refreshDetail(detailRow.receipt_id, 'OPEN');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set quality');
    } finally { setResolvingUsn(''); }
  };

  // Remove a USN scanned by mistake / not physically in this return.
  const removeUsn = async (usnCode) => {
    setResolvingUsn(usnCode);
    try {
      await apiClient.delete(`/sales-return/receipts/${detailRow.receipt_id}/usns/${usnCode}`);
      toast.success(`Removed ${usnCode}`);
      await refreshDetail(detailRow.receipt_id, 'OPEN');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove USN');
    } finally { setResolvingUsn(''); }
  };

  const finalizeReceipt = async () => {
    setFinalizing(true);
    try {
      await apiClient.post(`/sales-return/receipts/${detailRow.receipt_id}/finalize`);
      toast.success(`${detailRow.receipt_id} finalized`);
      setDetailOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Finalize failed', { duration: 9000 });
      await fetchBlockers(detailRow.receipt_id);
    } finally { setFinalizing(false); }
  };

  const resetPageAnd = (fn) => (v) => { setSkip(0); fn(v); };

  // ---- Printer helpers (mirror Bundle Receipt Entry) ----
  const connectPrinter = async () => {
    try {
      const device = await printerApi.requestUSBPrinter();
      setUsbDevice(device);
      toast.success(`Connected to ${device.productName || 'USB Printer'}`);
      return device;
    } catch (err) {
      toast.error('Failed to connect USB printer. Please try again.');
      return null;
    }
  };

  const changePrintMethod = (m) => {
    setPrintMethod(m);
    localStorage.setItem('sr_print_method', m);
  };

  const ensureNetPrinter = (forcePicker = false) => {
    if (!forcePicker && netPrinter) return Promise.resolve(netPrinter);
    return new Promise((resolve) => {
      printerPickerResolve.current = resolve;
      setPrinterPickerOpen(true);
    });
  };

  const handlePrinterPicked = (sel) => {
    const selection = { agentId: sel.agentId, printerName: sel.printerName, agentName: sel.agentName };
    setNetPrinter(selection);
    localStorage.setItem('sr_net_printer', JSON.stringify(selection));
    networkPrintApi.setPrintPreference({ agentId: sel.agentId, printerName: sel.printerName }).catch(() => {});
    setPrinterPickerOpen(false);
    if (printerPickerResolve.current) { printerPickerResolve.current(selection); printerPickerResolve.current = null; }
  };

  const handlePrinterDialogClose = (open) => {
    if (open) return;
    setPrinterPickerOpen(false);
    if (printerPickerResolve.current) { printerPickerResolve.current(null); printerPickerResolve.current = null; }
  };

  const sendBlobToPrinter = async (blob, { labelType = null, meta = null, forcePicker = false } = {}) => {
    if (printMethod === 'network') {
      let sel = netPrinter;
      if (forcePicker || !sel) {
        sel = await ensureNetPrinter(true);
        if (!sel) return { success: false, error: 'No printer selected' };
      }
      try {
        return await networkPrintApi.printViaAgent({
          agentId: sel.agentId, printerName: sel.printerName, payload: blob, labelType, meta,
        });
      } catch (e) {
        return { success: false, error: e.response?.data?.detail || e.message || 'Network print error' };
      }
    }
    let device = usbDevice;
    if (forcePicker || !device) {
      device = await connectPrinter();
      if (!device) return { success: false, error: 'No USB printer connected' };
    }
    try {
      await printerApi.sendToUSBPrinter(device, blob);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message || 'USB print error' };
    }
  };

  // Passive reprint — reprints the EXISTING stickers for a SKU (same USN codes,
  // same MRP, no DB change). Works on finalized receipts.
  const handleReprintExisting = async (item) => {
    if (!item.sku_code) {
      toast.error('This item has no FG/SKU assigned — nothing to reprint.');
      return;
    }
    if (printMethod === 'usb') {
      if (!usbDevice) {
        const d = await connectPrinter();
        if (!d) return;
      }
    } else {
      const sel = await ensureNetPrinter(false);
      if (!sel) return;
    }

    setPrintingSku(item.sku_code);
    try {
      // pricing_type intentionally empty → backend reproduces each piece's
      // original MRP (per-piece groups by stored pricing type, else TN).
      const res = await apiClient.post(
        `/sales-return/receipts/${detailRow.receipt_id}/reprint-existing-labels`,
        { sku_code: item.sku_code, pricing_type: '' },
      );
      const { usns, template, box_template, is_combo, groups } = res.data;
      if (!usns?.length) {
        toast.error('No pieces found to reprint for this item.');
        return;
      }
      // Prefer per-piece groups (exact original MRP); fall back to a single group.
      const printGroups = (groups && groups.length)
        ? groups
        : [{ usns, label_data: res.data.label_data, mrp_formatted: res.data.mrp_formatted }];
      const printerConfig = PRINTER_CONFIGS[printerModel];
      let blob = '';
      printGroups.forEach((g) => {
        (g.usns || []).forEach((u) => {
          blob += generateTsplFromLabelData(g.label_data, template, u, g.mrp_formatted, {
            printerConfig,
            arialLineSpacingMultiplier,
            templateType: template?.template_type,
          });
        });
      });
      const printRes = await sendBlobToPrinter(blob, {
        labelType: 'main_label',
        meta: { sku_code: item.sku_code, receipt_id: detailRow.receipt_id, count: usns.length, passive_reprint: true },
      });
      if (printRes.success) {
        toast.success(`Reprinted ${usns.length} sticker(s) for ${item.sku_code}`);
      } else {
        toast.error(`Print failed: ${printRes.error}`, { duration: 10000 });
        return;
      }

      if (is_combo && box_template) {
        setPendingBoxPrint({
          sku: item.sku_code,
          boxTemplate: box_template,
          groups: printGroups,
          count: usns.length,
        });
        toast.message(`Now reprint BOX stickers for ${item.sku_code}.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reprint failed', { duration: 10000 });
    } finally {
      setPrintingSku(null);
    }
  };

  // Resolve the box template to one that actually has a coordinate layout.
  // The reprint API returns the item's master box template, but if it ever
  // comes back without field positions we re-pull the full template from the
  // master (that's where the layout lives). Returns null when no usable layout
  // exists so the caller can refuse to print a cramped/overlapping box label.
  const resolveBoxTemplate = async (boxTemplate) => {
    if (templateHasLayout(boxTemplate)) return boxTemplate;
    const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    try {
      const res = await apiClient.get('/main-labels/templates');
      const all = res.data?.templates || [];
      const want = norm(boxTemplate?.template_name);
      const match = all.find((t) => norm(t.template_name) === want && templateHasLayout(t));
      if (match) return match;
    } catch (_) { /* fall through */ }
    return null;
  };

  const handleReprintBoxStickers = async (useDifferentPrinter = false) => {
    if (!pendingBoxPrint) return;
    // Always print box stickers using the MASTER box template's field layout.
    // Never fall back to product-default coordinates (which cram/overlap fields).
    const boxTpl = await resolveBoxTemplate(pendingBoxPrint.boxTemplate);
    if (!boxTpl) {
      toast.error(
        `Box template "${pendingBoxPrint.boxTemplate?.template_name || '—'}" has no label layout configured in the master. Set its field positions in Admin → Main Label Templates, then reprint.`,
        { duration: 12000 },
      );
      return;
    }
    setPrintingSku(pendingBoxPrint.sku);
    try {
      const printerConfig = PRINTER_CONFIGS[printerModel];
      let blob = '';
      (pendingBoxPrint.groups || []).forEach((g) => {
        (g.usns || []).forEach((u) => {
          blob += generateTsplFromLabelData(
            g.label_data, boxTpl, u, g.mrp_formatted,
            { printerConfig, arialLineSpacingMultiplier, templateType: boxTpl?.template_type },
          );
        });
      });
      const printRes = await sendBlobToPrinter(blob, {
        labelType: 'box_sticker',
        meta: { sku_code: pendingBoxPrint.sku, count: pendingBoxPrint.count, passive_reprint: true },
        forcePicker: useDifferentPrinter,
      });
      if (printRes.success) {
        toast.success(`Reprinted ${pendingBoxPrint.count} box sticker(s) for ${pendingBoxPrint.sku}`);
      } else {
        toast.error(`Box sticker print error: ${printRes.error}`, { duration: 10000 });
      }
    } catch (err) {
      toast.error(`Box sticker print error: ${err.message || err}`, { duration: 10000 });
    } finally {
      setPendingBoxPrint(null);
      setPrintingSku(null);
    }
  };

  const handleSkipBox = () => setPendingBoxPrint(null);

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="sr-transactions-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/sections/sales-return')}
            className="text-[#F9FAFB] hover:bg-white/10" data-testid="sr-transactions-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2"><FileText className="w-5 h-5" /> Completed Transactions</h1>
            <p className="text-sm text-[#E8DCC8]">All sales-return receipts — search &amp; filter</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        <Card>
          <CardContent className="py-4 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <div className="text-xs text-gray-500 mb-1">Search</div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                <Input placeholder="Return no, customer, place, agent, transporter…" value={search}
                  onChange={(e) => resetPageAnd(setSearch)(e.target.value)} className="pl-8" data-testid="sr-search-input" />
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Status</div>
              <Select value={status} onValueChange={resetPageAnd(setStatus)}>
                <SelectTrigger className="h-10 w-40" data-testid="sr-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="OPEN">Open</SelectItem>
                  <SelectItem value="FINALIZED">Finalized</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">From</div>
              <Input type="date" value={fromDate} onChange={(e) => resetPageAnd(setFromDate)(e.target.value)} className="h-10 w-40" data-testid="sr-from-date" />
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">To</div>
              <Input type="date" value={toDate} onChange={(e) => resetPageAnd(setToDate)(e.target.value)} className="h-10 w-40" data-testid="sr-to-date" />
            </div>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="sr-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </CardContent>
        </Card>

        <div className="text-sm text-gray-500">{total} transaction(s)</div>

        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="sr-transactions-table">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Return No</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Place</th>
                <th className="text-left px-3 py-2">Agent</th>
                <th className="text-left px-3 py-2">Transporter</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-right px-3 py-2">Bundles</th>
                <th className="text-right px-3 py-2">USNs</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">View</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.receipt_id} className="border-t hover:bg-gray-50" data-testid={`sr-row-${r.receipt_id}`}>
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{r.receipt_id}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2">{r.customer_name || '—'}</td>
                  <td className="px-3 py-2">{r.place || '—'}</td>
                  <td className="px-3 py-2">{r.agent_name || '—'}</td>
                  <td className="px-3 py-2">{r.transporter_name || '—'}</td>
                  <td className="px-3 py-2">{(r.return_reason || '').replace(/_/g, ' ')}</td>
                  <td className="px-3 py-2 text-right">{r.bundle_count}</td>
                  <td className="px-3 py-2 text-right">{r.usn_count}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={STATUS_STYLES[r.status] || 'text-gray-500'}>{r.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openDetail(r)} data-testid={`sr-view-${r.receipt_id}`}>
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="11" className="text-center py-10 text-gray-400" data-testid="sr-empty">No transactions match these filters.</td></tr>
              )}
              {loading && (
                <tr><td colSpan="11" className="text-center py-10 text-gray-400"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} data-testid="sr-prev">Previous</Button>
          <span className="text-xs text-gray-500">{total === 0 ? 0 : skip + 1}–{Math.min(skip + LIMIT, total)} of {total}</span>
          <Button variant="outline" size="sm" disabled={skip + LIMIT >= total} onClick={() => setSkip(skip + LIMIT)} data-testid="sr-next">Next</Button>
        </div>
      </main>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl" data-testid="sr-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#2D5F4F]" /> {detailRow?.receipt_id}
              {detailRow && <Badge variant="outline" className={STATUS_STYLES[detailRow.status] || ''}>{detailRow.status}</Badge>}
            </DialogTitle>
            <DialogDescription>Sales-return transaction details</DialogDescription>
          </DialogHeader>

          {detailRow && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
              <div><span className="text-gray-500">Customer:</span> <strong>{detailRow.customer_name || '—'}</strong></div>
              <div><span className="text-gray-500">Place:</span> {detailRow.place || '—'}</div>
              <div><span className="text-gray-500">Agent:</span> {detailRow.agent_name || '—'}</div>
              <div><span className="text-gray-500">Transporter:</span> {detailRow.transporter_name || '—'}</div>
              <div><span className="text-gray-500">Reason:</span> {(detailRow.return_reason || '').replace(/_/g, ' ')}</div>
              <div><span className="text-gray-500">Freight:</span> ₹{detailRow.freight_charge || 0}</div>
              <div><span className="text-gray-500">Created:</span> {fmtDate(detailRow.created_at)}</div>
              <div><span className="text-gray-500">Finalized:</span> {fmtDate(detailRow.finalized_at)}</div>
            </div>
          )}

          {detailRow?.status === 'OPEN' && !detailLoading && (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="sr-resolve-panel">
              {/* Floor accountability — verify a Staff ID before resolving */}
              {resolveStaffId ? (
                <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-amber-200">
                  <span className="inline-flex items-center gap-1.5 text-sm text-emerald-800" data-testid="sr-staff-verified">
                    <UserRound className="w-4 h-4" />
                    Acting as <strong>{resolveStaffName || resolveStaffId}</strong>
                    <span className="text-gray-500 font-mono text-xs">({resolveStaffId})</span>
                  </span>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-500 hover:text-amber-700"
                    onClick={changeResolveStaff} data-testid="sr-change-staff-btn">Change</Button>
                </div>
              ) : (
                <div className="mb-3 pb-3 border-b border-amber-200">
                  <div className="text-xs font-medium text-amber-800 mb-1.5">Scan/enter your Staff ID to resolve pieces</div>
                  <div className="flex items-center gap-2">
                    <Input autoFocus value={staffInput}
                      onChange={(e) => setStaffInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === 'Enter' && verifyStaff()}
                      placeholder="e.g. V0104" className="h-8 font-mono max-w-[200px]"
                      disabled={verifyingStaff} data-testid="sr-staff-input" />
                    <Button size="sm" className="h-8 bg-[#2D5F4F] hover:bg-[#24503f] text-white"
                      onClick={verifyStaff} disabled={verifyingStaff || !staffInput.trim()} data-testid="sr-staff-verify-btn">
                      {verifyingStaff ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Verify'}
                    </Button>
                  </div>
                </div>
              )}
              {blockers && (blockers.untagged_count > 0 || blockers.needs_fg_count > 0) ? (
                <>
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-800" data-testid="sr-blockers-heading">
                    <AlertTriangle className="w-4 h-4" />
                    {blockers.untagged_count + blockers.needs_fg_count} piece(s) block finalize
                  </div>
                  <p className="text-xs text-amber-700 mt-1">
                    Grade each piece, or remove it if it was scanned by mistake / isn't physically in this return.
                  </p>
                  <div className="mt-2 max-h-48 overflow-y-auto divide-y divide-amber-200">
                    {(blockers.untagged || []).map((u) => (
                      <div key={u.usn_code} className="flex items-center justify-between gap-2 py-2" data-testid={`sr-untagged-${u.usn_code}`}>
                        <div className="text-sm min-w-0">
                          <span className="font-mono">{u.usn_code}</span>
                          <span className="text-gray-500 ml-2">{u.sku_code || ''}{u.design_name ? ` · ${u.design_name}` : ''}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="sm" variant="outline" className="h-7 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                            disabled={!resolveStaffId || resolvingUsn === u.usn_code} onClick={() => gradeUsn(u.usn_code, 'PERFECT')} data-testid={`sr-grade-perfect-${u.usn_code}`}>Perfect</Button>
                          <Button size="sm" variant="outline" className="h-7 border-red-400 text-red-700 hover:bg-red-50"
                            disabled={!resolveStaffId || resolvingUsn === u.usn_code} onClick={() => gradeUsn(u.usn_code, 'DAMAGED')} data-testid={`sr-grade-damaged-${u.usn_code}`}>Damaged</Button>
                          <Button size="sm" variant="outline" className="h-7 border-orange-400 text-orange-700 hover:bg-orange-50"
                            disabled={!resolveStaffId || resolvingUsn === u.usn_code} onClick={() => gradeUsn(u.usn_code, 'REFINISH')} data-testid={`sr-grade-refinish-${u.usn_code}`}>Refinish</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-gray-500 hover:text-red-600 hover:bg-red-50"
                            disabled={!resolveStaffId || resolvingUsn === u.usn_code} onClick={() => removeUsn(u.usn_code)} data-testid={`sr-remove-${u.usn_code}`}>
                            {resolvingUsn === u.usn_code ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </Button>
                        </div>
                      </div>
                    ))}
                    {(blockers.needs_fg || []).map((u) => (
                      <div key={u.usn_code} className="flex items-center justify-between gap-2 py-2 text-sm" data-testid={`sr-needsfg-${u.usn_code}`}>
                        <span className="font-mono">{u.usn_code}</span>
                        <span className="text-xs text-amber-700">Needs FG assignment — resolve on the scanning page</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 text-sm font-medium text-emerald-700" data-testid="sr-ready-finalize">
                  <CheckCircle2 className="w-4 h-4" /> All pieces graded — ready to finalize.
                </div>
              )}
              <div className="mt-3 flex justify-end">
                <Button onClick={finalizeReceipt}
                  disabled={finalizing || !resolveStaffId || !blockers || blockers.untagged_count > 0 || blockers.needs_fg_count > 0}
                  className="bg-[#2D5F4F] hover:bg-[#24503f] text-white" data-testid="sr-finalize-btn">
                  {finalizing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  Finalize receipt
                </Button>
              </div>
            </div>
          )}

          <div className="mt-2 border-t pt-3">
            {detailLoading ? (
              <div className="flex items-center justify-center py-6 text-gray-500"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading items…</div>
            ) : detailData ? (
              <>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Package className="w-4 h-4" /> Items ({detailData.items?.length || 0}) · {detailData.total_usns} piece(s)
                  </div>
                  {/* Print destination + connect control for passive reprint */}
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex rounded-md overflow-hidden border border-gray-300" data-testid="sr-print-method-toggle">
                      <button type="button" onClick={() => changePrintMethod('usb')}
                        className={`px-2.5 py-1 text-xs flex items-center gap-1 ${printMethod === 'usb' ? 'bg-[#1A1F2E] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                        data-testid="sr-print-method-usb">
                        <Usb className="w-3 h-3" /> USB
                      </button>
                      <button type="button" onClick={() => changePrintMethod('network')}
                        className={`px-2.5 py-1 text-xs flex items-center gap-1 ${printMethod === 'network' ? 'bg-[#1A1F2E] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                        data-testid="sr-print-method-network">
                        <NetworkIcon className="w-3 h-3" /> Network
                      </button>
                    </div>
                    {printMethod === 'usb' ? (
                      usbDevice ? (
                        <Badge variant="outline" className="border-green-500 text-green-600" data-testid="sr-printer-connected-badge">
                          <Usb className="w-3 h-3 mr-1" />{usbDevice.productName || 'Printer connected'}
                        </Badge>
                      ) : (
                        <Button variant="outline" size="sm" onClick={connectPrinter} disabled={!usbSupported}
                          className="border-yellow-500 text-yellow-600 hover:bg-yellow-50" data-testid="sr-connect-printer-btn">
                          <Usb className="w-3 h-3 mr-1" /> Connect Printer
                        </Button>
                      )
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => ensureNetPrinter(true)}
                        className="border-cyan-500 text-cyan-600 hover:bg-cyan-50" data-testid="sr-select-network-printer-btn">
                        <Printer className="w-3 h-3 mr-1" />{netPrinter?.printerName || 'Select network printer'}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="max-h-[40vh] overflow-y-auto border rounded">
                  <table className="w-full text-sm" data-testid="sr-detail-items-table">
                    <thead className="bg-gray-50 text-gray-600 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">SKU</th>
                        <th className="text-left px-3 py-2">Design</th>
                        <th className="text-left px-3 py-2">Shade</th>
                        <th className="text-left px-3 py-2">Size</th>
                        <th className="text-right px-3 py-2">Qty</th>
                        <th className="text-right px-3 py-2">Perfect</th>
                        <th className="text-right px-3 py-2">Damaged</th>
                        <th className="text-right px-3 py-2">Refinish</th>
                        <th className="text-right px-3 py-2">Reprint</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detailData.items || []).map((it, idx) => (
                        <tr key={`${it.sku_code}-${idx}`} className="border-t">
                          <td className="px-3 py-2 font-mono text-xs">{it.sku_code || '—'}</td>
                          <td className="px-3 py-2">{it.design_name || '—'}</td>
                          <td className="px-3 py-2">{it.shade || '—'}</td>
                          <td className="px-3 py-2">{it.size || '—'}</td>
                          <td className="px-3 py-2 text-right font-medium">{it.count}</td>
                          <td className="px-3 py-2 text-right">{it.perfect}</td>
                          <td className="px-3 py-2 text-right">{it.damaged}</td>
                          <td className="px-3 py-2 text-right">{it.refinish}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              {it.is_combo && (
                                <Badge variant="outline" className="border-purple-300 text-purple-600 text-[10px] px-1.5 py-0"
                                  data-testid={`sr-combo-${it.sku_code}`}>
                                  <Boxes className="w-3 h-3 mr-0.5" /> Combo
                                </Badge>
                              )}
                              <Button size="sm" variant="outline"
                                disabled={!it.sku_code || printingSku === it.sku_code}
                                onClick={() => handleReprintExisting(it)}
                                className="h-7 border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F]/10"
                                data-testid={`sr-reprint-${it.sku_code || idx}`}>
                                {printingSku === it.sku_code
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <Printer className="w-3.5 h-3.5 mr-1" />}
                                Reprint
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(detailData.items || []).length === 0 && (
                        <tr><td colSpan="9" className="text-center py-6 text-gray-400">No pieces scanned on this receipt.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* COMBO BOX-STICKER — second reprint step, optionally on a different printer */}
      <Dialog open={!!pendingBoxPrint} onOpenChange={(open) => { if (!open) handleSkipBox(); }}>
        <DialogContent data-testid="sr-box-sticker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Boxes className="w-5 h-5 text-purple-600" /> Reprint BOX stickers — {pendingBoxPrint?.sku}
            </DialogTitle>
            <DialogDescription>This is a combo item — reprint its box stickers too.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Product stickers are printed. This is a <strong>combo</strong> item, so it also
              needs <strong>{pendingBoxPrint?.count || 0}</strong> box sticker(s) using
              template <strong>{pendingBoxPrint?.boxTemplate?.template_name || '—'}</strong>.
            </p>
            <p className="text-gray-500">
              Load the box-sticker roll. You can print on the same printer, or pick a different printer.
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleSkipBox} data-testid="sr-box-skip-btn">Skip</Button>
            <Button variant="outline" onClick={() => handleReprintBoxStickers(true)}
              disabled={printingSku === pendingBoxPrint?.sku} data-testid="sr-box-different-printer-btn">
              <Printer className="w-4 h-4 mr-1" /> Use a different printer
            </Button>
            <Button onClick={() => handleReprintBoxStickers(false)}
              disabled={printingSku === pendingBoxPrint?.sku}
              className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="sr-box-print-btn">
              {printingSku === pendingBoxPrint?.sku
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <Printer className="w-4 h-4 mr-1" />}
              Print Box Stickers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Network printer picker (Print Bridge) */}
      <NetworkPrinterDialog
        open={printerPickerOpen}
        onOpenChange={handlePrinterDialogClose}
        onConfirm={handlePrinterPicked}
        title="Select a network printer"
      />
    </div>
  );
};

export default SalesReturnTransactions;
